"use client";

import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  styled,
  Box,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion, AnimatePresence } from "framer-motion";
import { Roboto } from "next/font/google";
import ScrollReveal from "./ScrollReveal";
import { useTranslation } from "react-i18next";

const roboto = Roboto({
  subsets: ["latin"],
  weight: ["400", "500", "700"],
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  border: "1px solid #E2E8F0",
  borderRadius: "13px !important",
  marginBottom: "16px !important",
  "&:before": { display: "none" },
  background: "#FFFFFF",
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  minHeight: "auto",
  "&.Mui-expanded": { minHeight: "auto" },
  "& .MuiAccordionSummary-content": {
    margin: 0,
    "&.Mui-expanded": { margin: 0 },
  },
  "& .MuiAccordionSummary-expandIconWrapper": { color: "#64748B" },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 3, 2.5, 3),
  paddingTop: 0,
}));

const FAQS = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const faqsRaw = t("founderFaqs.faqs", { returnObjects: true });
  const faqs = Array.isArray(faqsRaw) ? faqsRaw : [];

  const [expanded, setExpanded] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const INITIAL_FAQ_COUNT = 5;

  const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_FAQ_COUNT);

  const handleChange = (index) => (event, isExpanded) => {
    setExpanded(isExpanded ? index : false);
  };

  return (
    <ScrollReveal delay={0.1} duration={1.2}>
      <Box sx={{ mb: -6, direction: isArabic ? "rtl" : "ltr" }}>
        <Container
          maxWidth="md"
          component={motion.div}
          initial="hidden"
          sx={{
            py: 8,
            mt: { xs: "1%", sm: "2%", md: "0%" },
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Heading Section */}
          <Box
            sx={{
              textAlign: isArabic
                ? {
                    xs: "right",
                    sm: "right",
                    md: "right",
                    lg: "center",
                    xl: "center",
                  }
                : {
                    xs: "left",
                    sm: "left",
                    md: "left",
                    lg: "center",
                    xl: "center",
                  },
              ml: isArabic ? 0 : "2%",
              mr: isArabic ? "2%" : 0,
              mb: 6,
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              fontWeight="700"
              sx={{
                mb: 1.2,
                fontSize: { xs: "1.7rem", sm: "2rem", md: "2.2rem" },
                color: "#111827",
                fontFamily: "var(--font-plusjakarta)",
              }}
            >
              {t("founderFaqs.title")}
            </Typography>
            <Typography
              sx={{
                color: "#4B5563",
                fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                fontFamily: "var(--font-outfit)",
                maxWidth: { lg: "80%", xl: "80%" },
                mx: { lg: "auto", xl: "auto" },
              }}
            >
              {t("founderFaqs.subtitle")}
            </Typography>
          </Box>

          {/* Accordion Section */}
          <AnimatePresence>
            {visibleFaqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <StyledAccordion
                  disableGutters
                  elevation={0}
                  expanded={expanded === index}
                  onChange={handleChange(index)}
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                  >
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontFamily: "var(--font-outfit)",
                        fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
                        color: "#111827",
                        textAlign: isArabic ? "right" : "left",
                      }}
                    >
                      {faq.question}
                    </Typography>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    <Typography
                      sx={{
                        fontFamily: "var(--font-outfit)",
                        fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                        color: "#64748B",
                        fontWeight: 400,
                        lineHeight: 1.7,
                        textAlign: isArabic ? "right" : "left",
                      }}
                    >
                      {faq.answer}
                    </Typography>
                  </StyledAccordionDetails>
                </StyledAccordion>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Load More Button */}
          {faqs.length > INITIAL_FAQ_COUNT && (
            <Box
              sx={{
                display: "flex",
                justifyContent: isArabic
                  ? {
                      xs: "flex-start",
                      sm: "flex-start",
                      md: "center",
                      lg: "center",
                    }
                  : {
                      xs: "flex-start",
                      sm: "flex-start",
                      md: "center",
                      lg: "center",
                    },
                mt: { xs: 3, sm: 4, md: 5 },
              }}
            >
              <Button
                component={motion.button}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAll(!showAll)}
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#5B6B77",
                  textTransform: "none",
                  px: 3,
                  py: 1.5,
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  fontWeight: "500",
                  borderRadius: "28px",
                  border: "1px solid #E2E8F0",
                  boxShadow: "none",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    bgcolor: "#FFFFFF",
                    boxShadow: "none",
                    transform: "scale(1.05)",
                  },
                }}
              >
                {showAll ? t("founderFaqs.seeLess") : t("founderFaqs.seeMore")}
              </Button>
            </Box>
          )}
        </Container>
      </Box>
    </ScrollReveal>
  );
};

export default FAQS;
